import { db } from './firebase';
import { collection, doc, setDoc, getDoc, updateDoc, query, where, getDocs, deleteField, serverTimestamp, addDoc } from 'firebase/firestore';

interface UserProgress {
  completedLessons: { [key: string]: boolean };
  subject?: string;
  slug?: string;
  careerTransitions?: {
    currentRole: string;
    desiredRole: string;
    topics: Array<{
      name: string;
      description: string;
      selected?: boolean;
    }>;
    timestamp: string;
  }[];
  modules?: Array<{
    title: string;
    lessons: string[];
  }>;
  preferences?: {
    level: string;
    goal: string;
    timeCommitment: string;
  };
}

interface LessonContent {
  content: string;
  moduleTitle: string;
  resourceLinks?: any[];
}

interface LessonCache {
  content: string;
  moduleTitle: string;
  timestamp: number;
  resourceLinks?: any[];
}

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = 'skillsprint_lesson_cache';

// In-memory cache
const lessonContentCache = new Map<string, LessonCache>();

// Load cached lessons from localStorage on initialization
try {
  const storedCache = localStorage.getItem(STORAGE_KEY);
  if (storedCache) {
    const parsedCache = JSON.parse(storedCache);
    Object.entries(parsedCache).forEach(([key, value]) => {
      lessonContentCache.set(key, value as LessonCache);
    });
  }
} catch (error) {
  console.error('Error loading cached lessons:', error);
}

function slugify(text: string): string {
  const timestamp = Date.now().toString(36);
  return `${text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')}-${timestamp}`;
}

// Save cache to localStorage
function persistCache() {
  try {
    const cacheObject: { [key: string]: LessonCache } = {};
    lessonContentCache.forEach((value, key) => {
      cacheObject[key] = value;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cacheObject));
  } catch (error) {
    console.error('Error saving lesson cache:', error);
  }
}

// Clean expired cache entries
function cleanCache() {
  const now = Date.now();
  let hasExpired = false;
  
  lessonContentCache.forEach((value, key) => {
    if (now - value.timestamp > CACHE_DURATION) {
      lessonContentCache.delete(key);
      hasExpired = true;
    }
  });

  if (hasExpired) {
    persistCache();
  }
}

export const firebaseService = {
  async saveProgress(userId: string, progress: UserProgress) {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, { 
        progress: { 
          completedLessons: progress.completedLessons,
          subject: progress.subject,
          slug: progress.slug,
          modules: progress.modules,
          preferences: progress.preferences
        }
      }, { merge: true });
    } catch (error) {
      console.error('Error saving progress:', error);
      throw error;
    }
  },

  async getProgress(userId: string): Promise<UserProgress | null> {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.roadmaps && data.progress?.slug) {
          const currentRoadmap = data.roadmaps.find((r: any) => r.slug === data.progress.slug);
          if (currentRoadmap) {
            return {
              ...data.progress,
              completedLessons: {
                ...data.progress.completedLessons,
                ...currentRoadmap.completedLessons
              }
            };
          }
        }
        return data.progress || null;
      }
      return null;
    } catch (error) {
      console.error('Error getting progress:', error);
      throw error;
    }
  },

  async saveCareerTransition(userId: string, data: {
    currentRole: string;
    desiredRole: string;
    topics: Array<{
      name: string;
      description: string;
    }>;
  }) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const transitions = userData.careerTransitions || [];
        
        const newTransition = {
          ...data,
          timestamp: new Date().toISOString()
        };

        await updateDoc(userRef, {
          careerTransitions: [newTransition, ...transitions].slice(0, 10) // Keep last 10 transitions
        });

        return newTransition;
      }
    } catch (error) {
      console.error('Error saving career transition:', error);
      throw error;
    }
  },

  async getCareerTransitions(userId: string) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const userData = docSnap.data();
        return userData.careerTransitions || [];
      }
      return [];
    } catch (error) {
      console.error('Error getting career transitions:', error);
      throw error;
    }
  },

  async updateLessonCompletion(userId: string, roadmapSlug: string, lessonId: string, completed: boolean) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const currentRoadmaps = data.roadmaps || [];
        
        const updatedRoadmaps = currentRoadmaps.map((roadmap: any) => {
          if (roadmap.slug === roadmapSlug) {
            return {
              ...roadmap,
              completedLessons: {
                ...roadmap.completedLessons,
                [lessonId]: completed
              }
            };
          }
          return roadmap;
        });

        const updateData: any = {
          roadmaps: updatedRoadmaps,
        };

        if (data.progress?.slug === roadmapSlug) {
          updateData['progress.completedLessons'] = {
            ...(data.progress?.completedLessons || {}),
            [lessonId]: completed
          };
        }

        await updateDoc(userRef, updateData);
      }
    } catch (error) {
      console.error('Error updating lesson completion:', error);
      throw error;
    }
  },

  async saveLessonContent(userId: string, roadmapSlug: string, lessonId: string, content: LessonContent) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const currentRoadmaps = data.roadmaps || [];
        
        const updatedRoadmaps = currentRoadmaps.map((roadmap: any) => {
          if (roadmap.slug === roadmapSlug) {
            return {
              ...roadmap,
              lessonContents: {
                ...(roadmap.lessonContents || {}),
                [lessonId]: content
              }
            };
          }
          return roadmap;
        });

        await updateDoc(userRef, {
          roadmaps: updatedRoadmaps
        });

        // Update local cache
        this.cacheLessonContent(lessonId, content.content, content.moduleTitle, content.resourceLinks);
      }
    } catch (error) {
      console.error('Error saving lesson content:', error);
      throw error;
    }
  },

  async getLessonContent(userId: string, roadmapSlug: string, lessonId: string): Promise<LessonContent | null> {
    // Check in-memory cache first
    const cachedContent = this.getLessonFromCache(lessonId);
    if (cachedContent) {
      return cachedContent;
    }

    // If not in cache, fetch from Firebase
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const roadmap = data.roadmaps?.find((r: any) => r.slug === roadmapSlug);
        
        if (roadmap?.lessonContents?.[lessonId]) {
          // Update cache with fetched content
          const content = roadmap.lessonContents[lessonId];
          this.cacheLessonContent(
            lessonId,
            content.content,
            content.moduleTitle,
            content.resourceLinks
          );
          return content;
        }
      }
      return null;
    } catch (error) {
      console.error('Error getting lesson content:', error);
      throw error;
    }
  },

  getLessonFromCache(lessonId: string): LessonContent | null {
    cleanCache();
    const cached = lessonContentCache.get(lessonId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return {
        content: cached.content,
        moduleTitle: cached.moduleTitle,
        resourceLinks: cached.resourceLinks
      };
    }
    return null;
  },

  cacheLessonContent(lessonId: string, content: string, moduleTitle: string, resourceLinks?: any[]) {
    lessonContentCache.set(lessonId, {
      content,
      moduleTitle,
      resourceLinks,
      timestamp: Date.now()
    });
    persistCache();
  },

  clearLessonCache() {
    lessonContentCache.clear();
    localStorage.removeItem(STORAGE_KEY);
  },

  async getAllUserRoadmaps(userId: string) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists() && docSnap.data().roadmaps) {
        return docSnap.data().roadmaps;
      }
      return [];
    } catch (error) {
      console.error('Error getting user roadmaps:', error);
      throw error;
    }
  },

  async saveRoadmap(userId: string, subject: string, modules: any[], preferences: any) {
    try {
      let slug = slugify(subject);
      const existingRoadmaps = await this.getAllUserRoadmaps(userId);
      
      const timestamp = new Date().toISOString();

      // Save to shared_roadmaps collection
      const sharedRoadmapRef = doc(db, 'shared_roadmaps', slug);
      await setDoc(sharedRoadmapRef, {
        subject,
        modules,
        preferences,
        createdAt: timestamp,
        createdBy: userId,
        viewCount: 0,
        cloneCount: 0,
        lastViewed: serverTimestamp()
      });

      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);

      const newRoadmap = {
        subject,
        slug,
        modules,
        preferences,
        completedLessons: {},
        lessonContents: {},
        createdAt: timestamp
      };

      await updateDoc(userRef, {
        roadmaps: [...existingRoadmaps, newRoadmap],
        progress: {
          subject,
          slug,
          modules,
          preferences,
          completedLessons: {}
        }
      });

      return slug;
    } catch (error) {
      console.error('Error saving roadmap:', error);
      throw error;
    }
  },

  async getSharedRoadmap(slug: string) {
    try {
      const sharedRoadmapRef = doc(db, 'shared_roadmaps', slug);
      const docSnap = await getDoc(sharedRoadmapRef);
      
      if (docSnap.exists()) {
        // Update view count and last viewed timestamp
        await updateDoc(sharedRoadmapRef, {
          viewCount: (docSnap.data().viewCount || 0) + 1,
          lastViewed: serverTimestamp()
        });
        
        return docSnap.data();
      }
      return null;
    } catch (error) {
      console.error('Error getting shared roadmap:', error);
      throw error;
    }
  },

  async cloneSharedRoadmap(userId: string, slug: string) {
    try {
      const sharedRoadmap = await this.getSharedRoadmap(slug);
      if (!sharedRoadmap) {
        throw new Error('Shared roadmap not found');
      }

      // Update clone count
      const sharedRoadmapRef = doc(db, 'shared_roadmaps', slug);
      await updateDoc(sharedRoadmapRef, {
        cloneCount: (sharedRoadmap.cloneCount || 0) + 1
      });

      // Save to user's roadmaps
      return await this.saveRoadmap(userId, sharedRoadmap.subject, sharedRoadmap.modules, sharedRoadmap.preferences);
    } catch (error) {
      console.error('Error cloning shared roadmap:', error);
      throw error;
    }
  },

  async deleteRoadmap(userId: string, slug: string) {
    try {
      const userRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedRoadmaps = (data.roadmaps || []).filter((r: any) => r.slug !== slug);
        
        if (data.roadmaps) {
          const roadmap = data.roadmaps.find((r: any) => r.slug === slug);
          if (roadmap) {
            roadmap.modules.forEach((module: any) => {
              module.lessons.forEach((lesson: string) => {
                lessonContentCache.delete(lesson);
              });
            });
            persistCache();
          }
        }

        const updateData: any = {
          roadmaps: updatedRoadmaps,
        };

        if (data.progress?.slug === slug) {
          updateData.progress = deleteField();
        }

        await updateDoc(userRef, updateData);
      }
    } catch (error) {
      console.error('Error deleting roadmap:', error);
      throw error;
    }
  },

  async getRoadmapBySlug(slug: string): Promise<{ userId: string; progress: UserProgress } | null> {
    try {
      const q = query(collection(db, 'users'), where('progress.slug', '==', slug));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          userId: doc.id,
          progress: doc.data().progress
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting roadmap by slug:', error);
      throw error;
    }
  },

  // Direct API calls to replace Supabase edge functions
  async generateRoadmap(subject: string, level: string, goal: string, timeCommitment: string) {
    try {
      const prompt = `As an expert educator specializing in creating accessible learning paths, design a structured roadmap for learning ${subject} that's suitable for a ${level} learner. The learner's goal is "${goal}" and can dedicate ${timeCommitment} per week.

      Important Guidelines:
      - Break down complex topics into digestible modules
      - Use clear, jargon-free language
      - Focus on practical, real-world applications
      - Consider the learner's time commitment (${timeCommitment})
      - Ensure progression is gradual and builds confidence
      - Include both foundational concepts and practical skills
      - Make content relatable to everyday experiences
      - Accommodate different learning styles

      Return only a raw JSON object with this structure:
      {
        "modules": [
          {
            "title": "module title",
            "lessons": ["lesson 1 title", "lesson 2 title", "lesson 3 title", "lesson 4 title", "lesson 5 title"]
          }
        ]
      }
      
      Guidelines for titles:
      - Use clear, action-oriented language
      - Make titles descriptive and engaging
      - Avoid technical jargon unless necessary
      - Keep titles concise but informative
      - Ensure titles reflect real-world value
      
      Include 5-7 modules, each with 5 specific lessons that progress logically. Focus on practical applications and real-world relevance. Return only the JSON object without any formatting.`;

      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'Authorization': `Bearer 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`
        },
        body: JSON.stringify({
          model: 'sonar-pro',
          messages: [
            { role: 'system', content: 'You are an expert educator. Be precise and concise.' },
            { role: 'user', content: prompt }
          ]
        })
      });

      const data = await response.json();
      if (data.choices?.[0]?.message?.content) {
        // Clean up markdown formatting before parsing JSON
        const content = data.choices[0].message.content;
        const jsonString = content.replace(/^```json\n|\n```$|^```\n|\n```$/g, '').trim();
        
        try {
          return JSON.parse(jsonString);
        } catch (parseError) {
          console.error('Failed to parse JSON response:', parseError);
          throw new Error('Invalid JSON response from API');
        }
      }
      throw new Error('No response from Perplexity API');
    } catch (error) {
      console.error('Error generating roadmap:', error);
      throw error;
    }
  },

  async generateLessonContent(lesson: string, moduleTitle: string, level: string, timeCommitment: string, goal: string) {
    try {
      const prompt = `You are a highly skilled learning designer and domain expert. Create content that is accurate, practical, and tailored to the learner's background and goal. Write with clarity, precision, and an encouraging tone—like a great instructor.
      
      Key Principles:
      1. Depth & Clarity: Provide thorough explanations while maintaining clarity
      2. Storytelling: Use narratives and real-world scenarios to explain concepts
      3. Progressive Learning: Build knowledge gradually from fundamentals to advanced topics
      4. Practical Context: Ground theory in real-world applications and examples
      5. Engagement: Keep learners motivated with interactive elements and relatable content

      Create a detailed lesson about "${lesson}" in the context of "${moduleTitle}" for a ${level} level learner who can commit ${timeCommitment} per week. The learner's goal is "${goal}". Ensure the level of depth and detail matches the learner's time commitment. For lower time, keep explanations tight and focused.

      # Learning Objectives
      Start with a clear narrative about what the learner will achieve. Then list 3-5 specific objectives that build upon each other. For each objective:
      - Explain why it's important
      - How it connects to the broader subject
      - What practical skills it will develop
      - How it relates to the learner's goals

      # Introduction  
      Tell a compelling story that:
      - Opens with a real-world scenario or problem
      - Explains why this topic matters
      - Connects to the learner's experiences
      - Sets up the key concepts to be learned
      - Creates curiosity and motivation

      # Core Concepts  
      Present each concept as a complete narrative:
      - Start with the fundamental idea
      - Explain the underlying principles
      - Provide detailed examples and illustrations
      - Connect to previous knowledge
      - Show how concepts build upon each other
      - Include relevant formulas or frameworks
      - Explain common misconceptions
      - Use metaphors and analogies for complex ideas

      > 💡 Quick Check: Add a brief reflection question here to help learners connect the concept to their experience

      # Practical Applications  
      Provide detailed, step-by-step guidance:
      - Start with a real problem or scenario
      - Break down the solution process
      - Explain each step thoroughly
      - Include examples from multiple contexts
      - Show variations and alternatives
      - Discuss potential challenges
      - Provide troubleshooting tips
      - Connect theory to practice

      > 🔨 Try This: Add a small, focused exercise that lets learners apply what they just learned

      # Best Practices  
      Create a comprehensive guide:
      - Explain each practice in detail
      - Provide the reasoning behind each recommendation
      - Include examples of good and bad approaches
      - Discuss trade-offs and considerations
      - Share expert insights and tips
      - Address common mistakes
      - Provide context-specific advice
      - Include real-world success stories

      > 💭 Reflect: Add a question that helps learners think about how these practices apply to their work

      # Common Challenges  
      Create detailed problem-solving guides:
      - Describe each challenge thoroughly
      - Explain why it occurs
      - Provide multiple solution approaches
      - Include step-by-step troubleshooting
      - Share prevention strategies
      - Discuss edge cases
      - Provide diagnostic techniques
      - Include real case studies

      > 🎯 Scenario: Present a realistic problem-solving scenario for learners to think through

      # Summary  
      Create a comprehensive review:
      - Summarize key concepts with explanations
      - Connect different parts of the lesson
      - Reinforce important principles
      - Review practical applications
      - Address common misconceptions
      - Suggest next steps for learning
      - Provide additional resources
      - Include thought-provoking questions

      # Practice Activities
      Design detailed learning exercises:
      - Start with clear objectives
      - Provide step-by-step instructions
      - Include example solutions
      - Offer variations for different skill levels
      - Suggest ways to extend the activity
      - Provide assessment criteria
      - Include troubleshooting tips
      - Connect to real-world scenarios

      Important guidelines:
      - Use clear headings (# for main sections, ## for subsections)
      - Provide detailed explanations for each concept
      - Add interactive elements (🤔 Think About It, ✍️ Try This, 💡 Quick Tip)
      - Include examples and illustrations
      - Only include verified, accurate information
      - Create smooth transitions between topics
      - Adapt depth to skill level: ${level}
      - Define technical terms clearly
      - Use storytelling to maintain engagement
      - Include diverse, inclusive examples
      - Balance theory and practice
      - Provide context for all concepts
      - Use clear, precise language
      - Include relevant citations or references`;

      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'Authorization': `Bearer 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`
        },
        body: JSON.stringify({
          model: 'sonar-pro',
          stream: true,
          messages: [
            { role: 'system', content: 'You are an expert educator. Be precise and concise.' },
            { role: 'user', content: prompt }
          ]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Error generating lesson content:', error);
      throw error;
    }
  },

  async generateResourceLinks(lesson: string, moduleTitle: string) {
    const defaultResources = [{
      title: 'MDN Web Docs',
      url: 'https://developer.mozilla.org',
      description: 'Access comprehensive documentation and learning resources from MDN Web Docs.'
    }];

    try {
      const prompt = `Generate a curated list of high-quality learning resources for the lesson "${lesson}" in the module "${moduleTitle}".

      You must return a valid JSON object with exactly this structure:
      {
        "resources": [
          {
            "title": "string",
            "url": "string (must start with http/https)",
            "description": "string"
          }
        ]
      }

      Critical Requirements:
      - Response MUST be a valid JSON object
      - MUST include the "resources" array
      - Each resource MUST have title, url, and description fields
      - URLs MUST start with http:// or https://
      - ONLY include resources from official documentation and well-known educational sites
      - NO hypothetical or generated URLs
      - Include 3-5 high-quality resources`;

      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'Authorization': `Bearer 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`
        },
        body: JSON.stringify({
          model: 'sonar-pro',
          messages: [
            { role: 'system', content: 'You are a JSON API that only returns valid JSON objects with the exact structure specified in the prompt. Never include explanatory text or markdown formatting.' },
            { role: 'user', content: prompt }
          ]
        })
      });

      const data = await response.json();
      let parsedData;
      try {
        const content = data.choices[0].message.content;
        const jsonString = content.replace(/^```json\n|\n```$|^```\n|\n```$/g, '').trim();
        parsedData = JSON.parse(jsonString);
      } catch (error) {
        console.error('Failed to parse resources JSON:', error);
        return defaultResources;
      }
      
      // Validate the response structure
      if (!parsedData || typeof parsedData !== 'object') {
        console.error('Invalid resources response structure');
        return defaultResources;
      }

      // Ensure we have a resources array
      if (!Array.isArray(parsedData.resources)) {
        console.error('Resources property is not an array');
        return defaultResources;
      }

      // Validate and filter each resource
      const validResources = parsedData.resources.filter((resource: any) => {
        if (!resource || typeof resource !== 'object') return false;
        
        const hasRequiredFields = 
          typeof resource.title === 'string' && resource.title.trim() !== '' &&
          typeof resource.url === 'string' && resource.url.trim() !== '' &&
          typeof resource.description === 'string' && resource.description.trim() !== '';
        
        const hasValidUrl = resource.url.startsWith('http') || resource.url.startsWith('https');
        
        return hasRequiredFields && hasValidUrl;
      });

      return validResources.length > 0 ? validResources : defaultResources;
    } catch (error) {
      console.error('Error generating resource links:', error);
      return defaultResources;
    }
  },

  async generateQuizQuestions(lesson: string, moduleTitle: string, content: string) {
    try {
      const prompt = `Create a quiz based on this lesson content: ${content}.
      
      Return as JSON:
      {
        "questions": [
          {
            "question": "Question text",
            "options": ["Option 1", "Option 2", "Option 3", "Option 4"],
            "correctAnswer": 0
          }
        ]
      }`;

      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'Authorization': `Bearer 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`
        },
        body: JSON.stringify({
          model: 'sonar-pro',
          messages: [
            { role: 'system', content: 'You are an expert educator. Be precise and concise.' },
            { role: 'user', content: prompt }
          ]
        })
      });

      const data = await response.json();
      const content_response = data.choices[0].message.content;
      const jsonString = content_response.replace(/^```json\n|\n```$|^```\n|\n```$/g, '').trim();
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error generating quiz questions:', error);
      return [];
    }
  },

  async generateTopics(currentRole: string, desiredRole: string) {
    try {
      const prompt = `As a career transition expert, analyze the transition from ${currentRole} to ${desiredRole}. Consider how the skills and experiences from their current role can be leveraged in their desired role. Identify key learning topics that build upon their existing knowledge while bridging the gap to their new career path.

      Return only a raw JSON object with this structure:
      {
        "message": "Encouraging message about their transition",
        "topics": [
          {
            "name": "Topic Name",
            "description": "Description connecting this topic to their current role and explaining its importance for the target role",
            "transferableSkills": ["List of related skills from current role that will help learn this topic"]
          }
        ]
      }

      Guidelines:
      - Highlight how their current skills and experience provide a strong foundation
      - Connect each topic to relevant aspects of their current role
      - Include both technical and transferable skills
      - Focus on building confidence through familiar concepts
      - Explain practical applications in the target role
      - List 4-6 key topics in order of priority
      - Keep descriptions encouraging and achievable`;

      const response = await fetch('https://api.perplexity.ai/chat/completions', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
          'Authorization': `Bearer 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`
        },
        body: JSON.stringify({
          model: 'sonar-pro',
          messages: [
            { role: 'system', content: 'You are an expert educator. Be precise and concise.' },
            { role: 'user', content: prompt }
          ]
        })
      });

      const data = await response.json();
      const content = data.choices[0].message.content;
      const jsonString = content.replace(/^```json\n|\n```$|^```\n|\n```$/g, '').trim();
      const response_data = JSON.parse(jsonString);
      
      return {
        message: response_data.message || "Your experience in " + currentRole + " provides valuable skills for becoming a " + desiredRole + "!",
        topics: response_data.topics.map((topic: any) => ({
          ...topic,
          transferableSkills: topic.transferableSkills || []
        }))
      };
    } catch (error) {
      console.error('Error generating topics:', error);
      throw error;
    }
  }
};